import { getUrl } from "../../../Utils/Common";
import { MOCK_PLANNER_EVENTS } from "../data/mockData";
import { bandToDiscountPct } from "../utils/promoDiscountBands";

/** Value sent in API bodies (e.g. `/promo/optimize`) when every brand is included */
export const PROMO_ALL_BRANDS_ID = "ALL";
export const PROMO_ALL_BRANDS_OPTION = { id: PROMO_ALL_BRANDS_ID, label: "All brands" };

export const PROMO_DEFAULT_MANUFACTURER = "SAVOLA GROUP";

export const PROMO_HISTORICAL_DEFAULT_YEAR = 2024;

export const PROMO_API_ALL_BRANDS_VALUE = "ALL_BRANDS";

export const PROMO_ALL_CLASSES_ID = "ALL";
export const PROMO_ALL_CLASSES_OPTION = { id: PROMO_ALL_CLASSES_ID, label: "All classes" };
export const PROMO_API_ALL_CLASSES_VALUE = "ALL_CLASSES";

export function resolvePerformanceBrandParam(brand) {
  if (brand == null || brand === "") {
    return PROMO_API_ALL_BRANDS_VALUE;
  }
  const trimmed = String(brand).trim();
  const upper = trimmed.toUpperCase();
  if (
    trimmed === PROMO_ALL_BRANDS_ID ||
    upper === "ALL" ||
    upper === PROMO_API_ALL_BRANDS_VALUE ||
    upper === "ALL BRANDS" ||
    trimmed === PROMO_ALL_BRANDS_OPTION.label
  ) {
    return PROMO_API_ALL_BRANDS_VALUE;
  }
  return trimmed;
}

/** Returns a trimmed class name for performance APIs, or null when none is selected. */
export function resolvePerformanceClassParam(productClass) {
  if (productClass == null || productClass === "") return null;
  const trimmed = String(productClass).trim();
  const upper = trimmed.toUpperCase();
  if (!trimmed || upper === "ALL" || upper === PROMO_API_ALL_CLASSES_VALUE || upper === "ALL CLASSES") return null;
  return trimmed;
}

function appendPerformanceClassParam(params, productClass) {
  const resolved = resolvePerformanceClassParam(productClass);
  if (resolved) params.set("class", resolved);
}

function mapApiHealthStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "healthy" || s === "weak" || s === "critical") return s;
  return "weak";
}

function mapApiCannibalRisk(level) {
  const l = String(level || "").toUpperCase();
  if (l === "LOW") return { cannibalizationRisk: "Low", cannibalRiskTier: "low" };
  if (l === "MED" || l === "MEDIUM") return { cannibalizationRisk: "Med", cannibalRiskTier: "med" };
  if (l === "HIGH") return { cannibalizationRisk: "High", cannibalRiskTier: "high" };
  return { cannibalizationRisk: "—", cannibalRiskTier: "none" };
}

/** Maps a single performance SKU row (sku-health / weak-skus) to UI shape */
export function mapPerformanceSkuRowFromApi(row, brandFallback = null) {
  if (!row || typeof row !== "object") return null;
  const cann = mapApiCannibalRisk(row.cannibalization_risk_level);
  const avgDiscRaw = row.avg_discount;
  let avgDiscount = null;
  if (avgDiscRaw != null && !Number.isNaN(Number(avgDiscRaw))) {
    const n = Number(avgDiscRaw);
    avgDiscount = n > 0 && n <= 1 ? n * 100 : n;
  }
  let promoDependencyPct = null;
  if (row.promo_dependency_pct != null && !Number.isNaN(Number(row.promo_dependency_pct))) {
    promoDependencyPct = Math.round(Number(row.promo_dependency_pct) * 100) / 100;
  } else if (row.promo_dependency != null && !Number.isNaN(Number(row.promo_dependency))) {
    promoDependencyPct = Math.round(Number(row.promo_dependency) * 10000) / 100;
  }
  const roi = row.actual_roi != null && !Number.isNaN(Number(row.actual_roi)) ? Number(row.actual_roi) : null;
  return {
    sku: row.sku_name ?? row.sku ?? "—",
    skuId: row.sku_id ?? null,
    brand: row.brand ?? brandFallback ?? "—",
    actualVolume: row.actual_volume_l ?? null,
    actualCM: row.actual_cm ?? null,
    actualCMEstimated: Boolean(row.actual_cm_estimated),
    volumeTrendStatus: row.volume_trend_status ? String(row.volume_trend_status).replace(/_/g, " ") : null,
    volumeTrendPct:
      row.volume_trend_pct != null && !Number.isNaN(Number(row.volume_trend_pct))
        ? Number(row.volume_trend_pct)
        : null,
    cmTrendPct:
      row.cm_trend_pct != null && !Number.isNaN(Number(row.cm_trend_pct))
        ? Number(row.cm_trend_pct)
        : null,
    avgRoi: roi,
    avgROI: roi,
    avgDiscount,
    promoDependencyPct,
    ...cann,
    cannibalizationRiskScore:
      row.cannibalization_risk_score != null && !Number.isNaN(Number(row.cannibalization_risk_score))
        ? Number(row.cannibalization_risk_score)
        : null,
    topCannibalizationPartner: row.top_cannibalization_partner ?? null,
    health: mapApiHealthStatus(row.health_status),
    healthScore: row.health_score ?? null,
    healthReasons: Array.isArray(row.health_reasons) ? row.health_reasons : [],
    promoWeeks: row.promo_weeks ?? null,
    activeWeeks: row.active_weeks ?? null,
    reasonMetric: row.reason_metric ?? null,
    class:
      row.class ??
      row.product_class ??
      row.sku_class ??
      row.category ??
      row.category_name ??
      null,
  };
}

/** Maps `/performance/sku-health` rows to Performance History table rows */
export function mapSkuHealthApiToRows(skuHealth, brandFallback = null) {
  if (!Array.isArray(skuHealth)) return [];
  return skuHealth.map((row) => mapPerformanceSkuRowFromApi(row, brandFallback)).filter(Boolean);
}

export const EMPTY_WEAK_SKU_RANKINGS = {
  declining: [],
  lowestRoi: [],
  highestPromoDep: [],
  highestCannibal: [],
};

/** Maps `/performance/weak-skus` `weak_skus` payload to ranking panel rows */
export function mapWeakSkusApiToRankings(weakSkus, brandFallback = null) {
  const w = weakSkus && typeof weakSkus === "object" ? weakSkus : {};
  const mapList = (arr) =>
    Array.isArray(arr) ? arr.map((row) => mapPerformanceSkuRowFromApi(row, brandFallback)).filter(Boolean) : [];
  return {
    declining: mapList(w.top_declining_skus),
    lowestRoi: mapList(w.lowest_roi_skus),
    highestPromoDep: mapList(w.highest_promo_dependency_skus),
    highestCannibal: mapList(w.highest_cannibalization_risk_skus),
  };
}

/** Maps `/performance/historical-trends` trend rows to weekly chart points */
export function mapHistoricalTrendsToWeeklyPoints(trends) {
  if (!Array.isArray(trends)) return [];
  return trends
    .map((t) => {
      const week = Number(t.week);
      if (!Number.isFinite(week)) return null;
      return {
        week,
        year: t.year != null ? Number(t.year) : null,
        weekLabel: t.week_label ?? null,
        volume: t.actual_volume_l ?? null,
        cm: t.actual_cm ?? null,
        roi: t.actual_roi ?? null,
        promoWeek: Boolean(t.promo_week),
        avgDiscount: t.avg_discount ?? null,
        promoSpend: t.promo_spend ?? null,
        actualValueSales: t.actual_value_sales ?? null,
        cmEstimated: Boolean(t.actual_cm_estimated),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.year != null && b.year != null && a.year !== b.year) return a.year - b.year;
      return a.week - b.week;
    });
}

function normalizeAvgDiscountDepthFromApi(raw) {
  if (raw == null || Number.isNaN(Number(raw))) return null;
  const n = Number(raw);
  return n > 0 && n <= 1 ? n * 100 : n;
}

/** Maps `/performance/promo-dependency` rows to Promo Dependency Analysis table rows */
export function mapPromoDependencyApiToRows(promoDependency) {
  if (!Array.isArray(promoDependency)) return [];
  return promoDependency
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const sku = row.sku_name ?? row.sku;
      if (!sku) return null;
      const dependencyPct =
        row.dependency_pct != null && !Number.isNaN(Number(row.dependency_pct))
          ? Math.round(Number(row.dependency_pct) * 100) / 100
          : 0;
      return {
        sku,
        dependencyPct,
        dependencyLevel: row.dependency_level ?? null,
        avgDiscountDepth: normalizeAvgDiscountDepthFromApi(row.avg_discount_depth),
        promoValueSales: Number(row.promo_value_sales) || 0,
        nonPromoValueSales: Number(row.non_promo_value_sales) || 0,
        promoVolumeL: Number(row.promo_volume_l) || 0,
        nonPromoVolumeL: Number(row.non_promo_volume_l) || 0,
        actualValueSales: row.actual_value_sales ?? null,
        actualVolumeL: row.actual_volume_l ?? null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (b.dependencyPct ?? 0) - (a.dependencyPct ?? 0));
}

/** Maps `/performance/historical-summary` summary payload to Historical KPI card fields */
export function mapHistoricalSummaryToKpi(summary) {
  if (!summary || typeof summary !== "object") return null;
  const avgDiscRaw = summary.avg_discount;
  let avgDiscountDepth = null;
  if (avgDiscRaw != null && !Number.isNaN(Number(avgDiscRaw))) {
    const n = Number(avgDiscRaw);
    avgDiscountDepth = n > 0 && n <= 1 ? n * 100 : n;
  }
  return {
    actualVolume: summary.actual_volume_l ?? null,
    actualCM: summary.actual_cm ?? null,
    actualCMEstimated: summary.actual_cm_estimated ?? false,
    actualROI: summary.actual_roi ?? null,
    promoSpend: summary.promo_spend ?? null,
    avgDiscountDepth,
    promoWeeks: summary.promo_weeks ?? null,
    activeWeeks: summary.active_weeks ?? null,
    skuCount: summary.sku_count ?? null,
    actualValueSales: summary.actual_value_sales ?? null,
  };
}

export const PROMO_OPTIMIZE_DEFAULT_CONSTRAINTS = {
  respect_cannibalization: true,
  respect_price_corridors: true,
};

export const PROMO_PLANNER_OPTIMIZE_DEFAULT_TARGETS = {
  target_volume_l: 12000,
  /** Contribution margin target as a percentage (dividend) from 0 to 100. */
  target_contribution_margin: 25,
};

export function buildOptimizeRequestBody({
  projectId,
  manufacturer = PROMO_DEFAULT_MANUFACTURER,
  brand,
  productClass,
  start_week,
  end_week,
  targetVolumeL,
  targetContributionMargin,
  constraints = PROMO_OPTIMIZE_DEFAULT_CONSTRAINTS,
  sku_names,
  includeTargets = true,
}) {
  const body = {
    project_id: Number(projectId),
    manufacturer: String(manufacturer).trim(),
    brand: resolvePerformanceBrandParam(brand),
    start_week: Number(start_week),
    end_week: Number(end_week),
    constraints: { ...PROMO_OPTIMIZE_DEFAULT_CONSTRAINTS, ...constraints },
  };
  if (includeTargets && targetVolumeL != null && targetContributionMargin != null) {
    body.targets = {
      target_volume_l: Number(targetVolumeL),
      target_contribution_margin: Number(targetContributionMargin),
    };
  }
  const hasSkuNames = Array.isArray(sku_names) && sku_names.length > 0;
  if (hasSkuNames) {
    body.sku_names = sku_names;
  } else {
    const resolvedClass = resolvePerformanceClassParam(productClass);
    if (resolvedClass) body.class = resolvedClass;
  }
  return body;
}

function weeksFromRange(startWeek, endWeek) {
  const start = Number(startWeek);
  const end = Number(endWeek);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function flatCalendarEntryToPlannerItem(entry) {
  if (!entry || entry.sku_name == null) return null;
  const week = Number(entry.week);
  if (!Number.isFinite(week) || week <= 0) return null;
  return {
    sku_name: entry.sku_name,
    week,
    selected_band: entry.selected_band,
    recommended_band: entry.recommended_band,
    brand: entry.brand,
    manufacturer: entry.manufacturer,
    must_promote: entry.must_promote,
    promo_type: entry.promo_type,
    row_type: "DETAIL",
    metrics: {
      expected_volume_l: entry.expected_volume_l,
      expected_profit: entry.expected_profit ?? entry.contribution_margin,
      discount_spend: entry.discount_spend,
      roi: entry.roi,
      uplift_ratio: entry.uplift_ratio,
      contribution_margin: entry.contribution_margin,
      contribution_margin_estimated: entry.contribution_margin_estimated,
      promo_price_pl: entry.promo_price_pl,
      promo_price_unit: entry.promo_price_unit,
      warnings: Array.isArray(entry.warnings) ? entry.warnings : [],
    },
    warnings: Array.isArray(entry.warnings) ? entry.warnings : [],
  };
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The optimizer backend may return bare `NaN` literals which are not valid JSON.
 * Parse the raw response text after replacing them with `null`.
 */
function safeParseOptimizerJson(text) {
  const sanitized = text.replace(/:\s*NaN\b/g, ": null");
  return JSON.parse(sanitized);
}

/** Maps promo API error codes to UI-safe copy  */
const PROMO_API_USER_MESSAGES = {
  SCENARIO_NOT_AVAILABLE:
    "There isn’t a matching promo scenario for this discount with the current rules. Try a different discount level or week.",
  INVALID_REQUEST:
    "No promotional history data was found for the selected SKUs in the chosen week range. Try a different week range or adjust your SKU selection.",
};

const PROMO_API_GENERIC_SIMULATION =
  "We couldn’t run this simulation. Try changing the discount, week, or product and run again.";
const PROMO_API_GENERIC_COMPARE =
  "We couldn’t load this comparison. Please try again in a moment.";

function tryParseJsonObject(str) {
  if (typeof str !== "string") return null;
  const t = str.trim();
  if (!t.startsWith("{")) return null;
  try {
    const p = JSON.parse(t);
    return p && typeof p === "object" ? p : null;
  } catch {
    return null;
  }
}

export function normalizeOptimizeApiResponse(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };

  // calendar_cells → optimized_calendar alias (new API shape)
  if (Array.isArray(out.calendar_cells) && !Array.isArray(out.optimized_calendar)) {
    out.optimized_calendar = out.calendar_cells;
  }

  // Promote result-level plan metrics into plan_summary for backwards-compat
  if (out.result && typeof out.result === "object") {
    if (!out.plan_summary) out.plan_summary = {};
    const r = out.result;
    if (out.plan_summary.actual_volume_total == null && r.plan_volume_l != null) {
      out.plan_summary.actual_volume_total = r.plan_volume_l;
    }
    if (out.plan_summary.actual_profit_total == null && r.plan_contribution_margin != null) {
      out.plan_summary.actual_profit_total = r.plan_contribution_margin;
    }
    if (out.plan_summary.total_contribution_margin == null && r.total_contribution_margin != null) {
      out.plan_summary.total_contribution_margin = r.total_contribution_margin;
    }
    out.plan_summary._volume_target_achieved = r.volume_target_achieved ?? null;
    out.plan_summary._cm_target_achieved = r.cm_target_achieved ?? null;
  }

  return out;
}

function normalizeStructuredPromoError(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (typeof obj.code === "string") return obj;
  return null;
}

function extractPromoApiErrorPayload(data) {
  if (!data || typeof data !== "object") return null;

  let hit = normalizeStructuredPromoError(data);
  if (hit) return hit;

  const candidates = [data.error, data.details];
  for (const c of candidates) {
    if (c && typeof c === "object") {
      hit = normalizeStructuredPromoError(c);
      if (hit) return hit;
    }
    const parsed = tryParseJsonObject(c);
    hit = normalizeStructuredPromoError(parsed);
    if (hit) return hit;
  }

  if (typeof data.message === "string") {
    const parsedMsg = tryParseJsonObject(data.message);
    hit = normalizeStructuredPromoError(parsedMsg);
    if (hit) return hit;
  }

  return null;
}

function promoApiUserMessage(data, fallbackGeneric) {
  const structured = extractPromoApiErrorPayload(data);
  if (structured?.code && PROMO_API_USER_MESSAGES[structured.code]) {
    return PROMO_API_USER_MESSAGES[structured.code];
  }
  // Prefer the backend's own message when there is no pre-written friendly copy
  if (structured?.code) {
    const backendMsg = typeof structured.message === "string" ? structured.message.trim() : "";
    return backendMsg || fallbackGeneric;
  }

  const plain =
    typeof data?.message === "string"
      ? data.message.trim()
      : typeof data?.error === "string"
        ? data.error.trim()
        : "";

  if (plain.startsWith("{")) {
    const parsed = tryParseJsonObject(plain);
    const nested = extractPromoApiErrorPayload(parsed) || normalizeStructuredPromoError(parsed);
    if (nested?.code && PROMO_API_USER_MESSAGES[nested.code]) {
      return PROMO_API_USER_MESSAGES[nested.code];
    }
    if (nested?.code) {
      const nestedMsg = typeof nested.message === "string" ? nested.message.trim() : "";
      return nestedMsg || fallbackGeneric;
    }
    return fallbackGeneric;
  }

  if (plain) return plain;

  return fallbackGeneric;
}

/** Throws an Error enriched with .apiCode / .apiMessage / .apiDiagnostics from the backend payload. */
function throwPromoApiError(data, fallbackGeneric) {
  const structured = extractPromoApiErrorPayload(data);
  const msg = promoApiUserMessage(data, fallbackGeneric);
  const err = new Error(msg);
  if (structured) {
    err.apiCode = structured.code ?? null;
    err.apiMessage = typeof structured.message === "string" ? structured.message.trim() : null;
    err.apiDiagnostics =
      structured.diagnostics && typeof structured.diagnostics === "object"
        ? structured.diagnostics
        : null;
  }
  throw err;
}

function promoAuthToken() {
  return (typeof localStorage !== "undefined" && localStorage.getItem("userId")) || "";
}

function promoRequestHeaders() {
  const token = promoAuthToken();
  return {
    "Content-Type": "application/json",
    "x-access-token": token,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
  };
}

function normalizeBrandEntry(entry) {
  if (entry == null) return null;
  if (typeof entry === "string") {
    const t = entry.trim();
    return t ? { id: t, label: t } : null;
  }
  if (typeof entry === "object") {
    const label =
      entry.label ??
      entry.name ??
      entry.brand ??
      entry.brand_name ??
      entry.brandName ??
      entry.display_name;
    const id = entry.id ?? entry.code ?? entry.brand_id ?? entry.brandId ?? label;
    if (label == null && id == null) return null;
    const idStr = String(id ?? label).trim();
    const labelStr = String(label ?? id).trim();
    if (!idStr && !labelStr) return null;
    return { id: idStr || labelStr, label: labelStr || idStr };
  }
  return null;
}

function brandsRawListFromPayload(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.brands)) return data.brands;
  if (Array.isArray(data.data?.brands)) return data.data.brands;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

function brandsFromApiPayload(data) {
  return brandsRawListFromPayload(data)
    .map(normalizeBrandEntry)
    .filter(Boolean);
}

function classesRawListFromPayload(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.classes)) return data.classes;
  if (Array.isArray(data.data?.classes)) return data.data.classes;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

function classesFromApiPayload(data) {
  return classesRawListFromPayload(data)
    .map(normalizeBrandEntry)
    .filter(Boolean);
}

function normalizeSkuEntry(item, index) {
  if (typeof item === "string") {
    return { id: item, label: item, basePrice: 0 };
  }
  if (!item || typeof item !== "object") return null;
  const id =
    item.id ??
    item.sku_id ??
    item.skuId ??
    item.code ??
    `sku_${index}`;
  const label =
    item.label ??
    item.name ??
    item.sku_name ??
    item.skuName ??
    String(id);
  const raw =
    item.basePrice ?? item.base_price ?? item.price ?? item.list_price ?? 0;
  const basePrice =
    typeof raw === "number" ? raw : Number.parseFloat(raw) || 0;
  return { id: String(id), label: String(label), basePrice };
}

function skusFromApiPayload(data) {
  const list = Array.isArray(data?.skus)
    ? data.skus
    : Array.isArray(data)
      ? data
      : [];
  return list
    .map((item, i) => normalizeSkuEntry(item, i))
    .filter(Boolean);
}

/**
 * Maps the `updated_cell` object from PATCH /promo/plan/{id}/cell into
 * a planner cell patch shape (same structure as individual cells in the grid).
 */
function normalizePlanCellResponse(data) {
  const cell = data?.updated_cell ?? {};
  const m = cell.metrics ?? {};
  const selectedDiscount =
    m.selected_discount != null && Number.isFinite(Number(m.selected_discount))
      ? Number(m.selected_discount)
      : null;
  const displayPct =
    selectedDiscount != null ? selectedDiscount * 100 : bandToDiscountPct(cell.selected_band ?? "");

  const cellPatch = {
    selectedBand: cell.selected_band ?? null,
    selectedDiscount,
    discountPct: displayPct,
    kpis: {
      expectedVolumeL: m.expected_volume_l ?? null,
      expectedProfit: m.expected_profit ?? null,
      discountSpend: m.discount_spend ?? null,
      roi: m.roi ?? null,
      upliftRatio: m.uplift_ratio ?? null,
      contributionMargin: m.contribution_margin ?? null,
      contributionMarginEstimated: m.contribution_margin_estimated ?? null,
      promoPricePl: m.promo_price_pl ?? null,
      promoPriceUnit: m.promo_price_unit ?? null,
      recommendedBand: cell.recommended_band ?? null,
      selectedBand: cell.selected_band ?? null,
      selectedDiscount,
      mustPromote: cell.must_promote,
      warnings: Array.isArray(m.warnings) ? m.warnings : [],
    },
  };

  return {
    cellPatch,
    planSummary: data.plan_summary ?? null,
    planId: data.plan_id ?? null,
    warnings: Array.isArray(data.warnings) ? data.warnings : [],
  };
}

export const promoSimulatorService = {
  getManufacturers: async (projectId) => {
    try {
      const params = new URLSearchParams();
      if (projectId != null && projectId !== "") {
        params.set("project_id", String(projectId));
      }
      const query = params.toString();
      const url = `${getUrl()}/catalog/manufacturers${query ? `?${query}` : ""}`;
      const res = await fetch(url, {
        method: "GET",
        headers: promoRequestHeaders(),
      });
      if (!res.ok) {
        throw new Error(`getManufacturers failed: ${res.status}`);
      }
      const data = await res.json();
      if (data?.success === false) return [];
      const list = Array.isArray(data?.manufacturers) ? data.manufacturers : [];
      return list.map((m) => String(m).trim()).filter(Boolean);
    } catch (e) {
      console.error("[promoSimulatorService] getManufacturers", e);
      return [];
    }
  },

  getBrands: async (manufacturer, productClass, projectId) => {
    try {
      const params = new URLSearchParams();
      if (manufacturer != null && String(manufacturer).trim()) {
        params.set("manufacturers", String(manufacturer).trim());
      }
      const resolvedClass = resolvePerformanceClassParam(productClass);
      if (resolvedClass) {
        params.set("class", resolvedClass);
      }
      if (projectId != null && projectId !== "") {
        params.set("project_id", String(projectId));
      }
      const query = params.toString().replace(/\+/g, "%20");
      const url = `${getUrl()}/catalog/brands${query ? `?${query}` : ""}`;
      console.log("[promoSimulatorService] getBrands →", url);
      const res = await fetch(url, {
        method: "GET",
        headers: promoRequestHeaders(),
      });
      if (!res.ok) {
        throw new Error(`getBrands failed: ${res.status}`);
      }
      const data = await res.json();
      if (data?.success === false) return [];
      const result = brandsFromApiPayload(data);
      console.log("[promoSimulatorService] getBrands ←", result);
      return result;
    } catch (e) {
      console.error("[promoSimulatorService] getBrands", e);
      return [];
    }
  },

  getClasses: async (brandName, manufacturer, projectId) => {
    try {
      const params = new URLSearchParams();
      if (manufacturer != null && String(manufacturer).trim()) {
        params.set("manufacturers", String(manufacturer).trim());
      }
      const resolvedBrand = resolvePerformanceBrandParam(brandName);
      if (resolvedBrand && resolvedBrand !== PROMO_API_ALL_BRANDS_VALUE) {
        params.set("brand", resolvedBrand);
      }
      if (projectId != null && projectId !== "") {
        params.set("project_id", String(projectId));
      }
      const query = params.toString().replace(/\+/g, "%20");
      const url = `${getUrl()}/catalog/class${query ? `?${query}` : ""}`;
      console.log("[promoSimulatorService] getClasses →", url);
      const res = await fetch(url, {
        method: "GET",
        headers: promoRequestHeaders(),
      });
      if (!res.ok) {
        throw new Error(`getClasses failed: ${res.status}`);
      }
      const data = await res.json();
      if (data?.success === false) return [];
      const result = classesFromApiPayload(data);
      console.log("[promoSimulatorService] getClasses ←", result);
      return result;
    } catch (e) {
      console.error("[promoSimulatorService] getClasses", e);
      return [];
    }
  },

  getSKUsByBrand: async (brandName, manufacturer, productClass) => {
    if (
      !brandName ||
      String(brandName).trim() === "" ||
      String(brandName).trim() === PROMO_ALL_BRANDS_ID
    ) {
      return [];
    }
    try {
      const params = new URLSearchParams();
      params.set("brand", String(brandName).trim());
      if (manufacturer != null && String(manufacturer).trim()) {
        params.set("manufacturer", String(manufacturer).trim());
      }
      appendPerformanceClassParam(params, productClass);
      const url = `${getUrl()}/promo/skus?${params.toString()}`;
      const res = await fetch(url, {
        method: "GET",
        headers: promoRequestHeaders(),
      });
      if (!res.ok) {
        throw new Error(`getSKUsByBrand failed: ${res.status}`);
      }
      const data = await res.json();
      if (data?.success === false) return [];
      return skusFromApiPayload(data);
    } catch (e) {
      console.error("[promoSimulatorService] getSKUsByBrand", e);
      return [];
    }
  },

  /**
   * @returns {{ weeks: number[], error: string | null }}
   */
  /**
   * GET /performance/historical-summary
   * @returns {Promise<{ success: boolean, summary?: object, warnings?: string[], filters?: object, error?: string }>}
   */
  getHistoricalSummary: async ({
    projectId,
    manufacturer = PROMO_DEFAULT_MANUFACTURER,
    brand,
    productClass,
    year = PROMO_HISTORICAL_DEFAULT_YEAR,
    startWeek = 1,
    endWeek = 52,
  } = {}) => {
    if (projectId == null || projectId === "") {
      return { success: false, error: "Project is not selected" };
    }
    try {
      const params = new URLSearchParams();
      params.set("project_id", String(projectId));
      params.set("manufacturer", String(manufacturer).trim());
      params.set("brand", resolvePerformanceBrandParam(brand));
      appendPerformanceClassParam(params, productClass);
      params.set("year", String(year));
      params.set("start_week", String(startWeek));
      params.set("end_week", String(endWeek));

      const res = await fetch(
        `${getUrl()}/performance/historical-summary?${params.toString()}`,
        {
          method: "GET",
          headers: promoRequestHeaders(),
        }
      );

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        const msg =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          `Historical summary request failed (${res.status})`;
        return { success: false, error: msg };
      }

      if (data?.success === false) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : "Historical summary request was not successful";
        return { success: false, error: msg };
      }

      return {
        success: true,
        summary: data?.summary ?? null,
        warnings: Array.isArray(data?.warnings) ? data.warnings : [],
        filters: data?.filters ?? null,
      };
    } catch (e) {
      console.error("[promoSimulatorService] getHistoricalSummary", e);
      return {
        success: false,
        error: e?.message || "Failed to load historical summary",
      };
    }
  },

  /**
   * GET /performance/historical-trends
   * @returns {Promise<{ success: boolean, trends?: object[], warnings?: string[], filters?: object, error?: string }>}
   */
  getHistoricalTrends: async ({
    projectId,
    manufacturer = PROMO_DEFAULT_MANUFACTURER,
    brand,
    productClass,
    year = PROMO_HISTORICAL_DEFAULT_YEAR,
    startWeek = 1,
    endWeek = 52,
    granularity = "week",
    skuName,
  } = {}) => {
    if (projectId == null || projectId === "") {
      return { success: false, error: "Project is not selected" };
    }
    try {
      const params = new URLSearchParams();
      params.set("project_id", String(projectId));
      params.set("manufacturer", String(manufacturer).trim());
      params.set("brand", resolvePerformanceBrandParam(brand));
      appendPerformanceClassParam(params, productClass);
      params.set("year", String(year));
      params.set("start_week", String(startWeek));
      params.set("end_week", String(endWeek));
      params.set("granularity", String(granularity).trim() || "week");
      if (skuName != null && String(skuName).trim()) {
        params.set("sku_name", String(skuName).trim());
      }

      const res = await fetch(
        `${getUrl()}/performance/historical-trends?${params.toString()}`,
        {
          method: "GET",
          headers: promoRequestHeaders(),
        }
      );

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        const msg =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          `Historical trends request failed (${res.status})`;
        return { success: false, error: msg };
      }

      if (data?.success === false) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : "Historical trends request was not successful";
        return { success: false, error: msg };
      }

      return {
        success: true,
        trends: Array.isArray(data?.trends) ? data.trends : [],
        warnings: Array.isArray(data?.warnings) ? data.warnings : [],
        filters: data?.filters ?? null,
      };
    } catch (e) {
      console.error("[promoSimulatorService] getHistoricalTrends", e);
      return {
        success: false,
        error: e?.message || "Failed to load historical trends",
      };
    }
  },

  /**
   * GET /performance/sku-health
   * @returns {Promise<{ success: boolean, skuHealth?: object[], warnings?: string[], filters?: object, error?: string }>}
   */
  getSkuHealth: async ({
    projectId,
    manufacturer = PROMO_DEFAULT_MANUFACTURER,
    brand,
    productClass,
    year = PROMO_HISTORICAL_DEFAULT_YEAR,
    startWeek = 1,
    endWeek = 52,
    skuName,
  } = {}) => {
    if (projectId == null || projectId === "") {
      return { success: false, error: "Project is not selected" };
    }
    try {
      const params = new URLSearchParams();
      params.set("project_id", String(projectId));
      params.set("manufacturer", String(manufacturer).trim());
      params.set("brand", resolvePerformanceBrandParam(brand));
      appendPerformanceClassParam(params, productClass);
      params.set("year", String(year));
      params.set("start_week", String(startWeek));
      params.set("end_week", String(endWeek));
      if (skuName != null && String(skuName).trim()) {
        params.set("sku_name", String(skuName).trim());
      }

      const res = await fetch(
        `${getUrl()}/performance/sku-health?${params.toString()}`,
        {
          method: "GET",
          headers: promoRequestHeaders(),
        }
      );

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        const msg =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          `SKU health request failed (${res.status})`;
        return { success: false, error: msg };
      }

      if (data?.success === false) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : "SKU health request was not successful";
        return { success: false, error: msg };
      }

      return {
        success: true,
        skuHealth: Array.isArray(data?.sku_health) ? data.sku_health : [],
        warnings: Array.isArray(data?.warnings) ? data.warnings : [],
        filters: data?.filters ?? null,
      };
    } catch (e) {
      console.error("[promoSimulatorService] getSkuHealth", e);
      return {
        success: false,
        error: e?.message || "Failed to load SKU health",
      };
    }
  },

  /**
   * GET /performance/weak-skus
   * @returns {Promise<{ success: boolean, weakSkus?: object, summary?: object, warnings?: string[], filters?: object, error?: string }>}
   */
  getWeakSkus: async ({
    projectId,
    manufacturer = PROMO_DEFAULT_MANUFACTURER,
    brand,
    productClass,
    year = PROMO_HISTORICAL_DEFAULT_YEAR,
    startWeek = 1,
    endWeek = 52,
    skuName,
  } = {}) => {
    if (projectId == null || projectId === "") {
      return { success: false, error: "Project is not selected" };
    }
    try {
      const params = new URLSearchParams();
      params.set("project_id", String(projectId));
      params.set("manufacturer", String(manufacturer).trim());
      params.set("brand", resolvePerformanceBrandParam(brand));
      appendPerformanceClassParam(params, productClass);
      params.set("year", String(year));
      params.set("start_week", String(startWeek));
      params.set("end_week", String(endWeek));
      if (skuName != null && String(skuName).trim()) {
        params.set("sku_name", String(skuName).trim());
      }

      const res = await fetch(
        `${getUrl()}/performance/weak-skus?${params.toString()}`,
        {
          method: "GET",
          headers: promoRequestHeaders(),
        }
      );

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        const msg =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          `Weak SKUs request failed (${res.status})`;
        return { success: false, error: msg };
      }

      if (data?.success === false) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : "Weak SKUs request was not successful";
        return { success: false, error: msg };
      }

      return {
        success: true,
        weakSkus: data?.weak_skus ?? null,
        summary: data?.summary ?? null,
        warnings: Array.isArray(data?.warnings) ? data.warnings : [],
        filters: data?.filters ?? null,
      };
    } catch (e) {
      console.error("[promoSimulatorService] getWeakSkus", e);
      return {
        success: false,
        error: e?.message || "Failed to load weak SKUs",
      };
    }
  },

  /**
   * GET /performance/promo-dependency
   * @returns {Promise<{ success: boolean, promoDependency?: object[], warnings?: string[], filters?: object, error?: string }>}
   */
  getPromoDependency: async ({
    projectId,
    manufacturer = PROMO_DEFAULT_MANUFACTURER,
    brand,
    productClass,
    year = PROMO_HISTORICAL_DEFAULT_YEAR,
    startWeek = 1,
    endWeek = 52,
    skuName,
  } = {}) => {
    if (projectId == null || projectId === "") {
      return { success: false, error: "Project is not selected" };
    }
    try {
      const params = new URLSearchParams();
      params.set("project_id", String(projectId));
      params.set("manufacturer", String(manufacturer).trim());
      params.set("brand", resolvePerformanceBrandParam(brand));
      appendPerformanceClassParam(params, productClass);
      params.set("year", String(year));
      params.set("start_week", String(startWeek));
      params.set("end_week", String(endWeek));
      if (skuName != null && String(skuName).trim()) {
        params.set("sku_name", String(skuName).trim());
      }

      const res = await fetch(
        `${getUrl()}/performance/promo-dependency?${params.toString()}`,
        {
          method: "GET",
          headers: promoRequestHeaders(),
        }
      );

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        const msg =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          `Promo dependency request failed (${res.status})`;
        return { success: false, error: msg };
      }

      if (data?.success === false) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : "Promo dependency request was not successful";
        return { success: false, error: msg };
      }

      return {
        success: true,
        promoDependency: Array.isArray(data?.promo_dependency) ? data.promo_dependency : [],
        warnings: Array.isArray(data?.warnings) ? data.warnings : [],
        filters: data?.filters ?? null,
      };
    } catch (e) {
      console.error("[promoSimulatorService] getPromoDependency", e);
      return {
        success: false,
        error: e?.message || "Failed to load promo dependency",
      };
    }
  },

  getPromoWeeks: async (brandName, skuName, manufacturer, productClass) => {
    if (
      !brandName ||
      !skuName ||
      String(brandName).trim() === "" ||
      String(brandName).trim() === PROMO_ALL_BRANDS_ID
    ) {
      return { weeks: [], error: null };
    }
    try {
      const params = new URLSearchParams();
      if (manufacturer != null && String(manufacturer).trim()) {
        params.set("manufacturer", String(manufacturer).trim());
      }
      params.set("brand", String(brandName).trim());
      appendPerformanceClassParam(params, productClass);
      params.set("sku", String(skuName).trim());
      const url = `${getUrl()}/promo/weeks?${params.toString()}`;
      const res = await fetch(url, {
        method: "GET",
        headers: promoRequestHeaders(),
      });
      if (!res.ok) {
        let detail = String(res.status);
        try {
          const errData = await res.json();
          const msg =
            (typeof errData?.message === "string" && errData.message) ||
            (typeof errData?.error === "string" && errData.error);
          if (msg) detail = msg;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const data = await res.json();
      if (data?.success === false) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : "Weeks request was not successful";
        return { weeks: [], error: msg };
      }
      const raw = Array.isArray(data?.weeks) ? data.weeks : [];
      const nums = raw
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n));
      return {
        weeks: [...new Set(nums)].sort((a, b) => a - b),
        error: null,
      };
    } catch (e) {
      console.error("[promoSimulatorService] getPromoWeeks", e);
      const msg = e?.message || "Unknown error";
      return {
        weeks: [],
        error:
          msg === "Failed to fetch"
            ? "Could not reach the server to load weeks."
            : `Could not load weeks: ${msg}`,
      };
    }
  },

  getPlannerEvents: async () => {
    await delay(300);
    return MOCK_PLANNER_EVENTS;
  },

  getOptimizerResults: async ({
    projectId,
    manufacturer = PROMO_DEFAULT_MANUFACTURER,
    brand,
    productClass,
    start_week,
    end_week,
    targetVolumeL,
    targetContributionMargin,
    sku_names,
  }) => {
    if (projectId == null || projectId === "") {
      throw new Error("Project is not selected");
    }
    if (!promoAuthToken()) {
      throw new Error("You need to be signed in to run the optimizer. Please log in and try again.");
    }

    // Constraints are static until there is UI for them
    const body = buildOptimizeRequestBody({
      projectId,
      manufacturer,
      brand,
      productClass,
      start_week,
      end_week,
      targetVolumeL,
      targetContributionMargin,
      constraints: PROMO_OPTIMIZE_DEFAULT_CONSTRAINTS,
      sku_names,
    });

    let res;
    try {
      res = await fetch(`${getUrl()}/promo/optimize`, {
        method: "POST",
        headers: promoRequestHeaders(),
        body: JSON.stringify(body),
      });
    } catch {
      throw new Error("Could not reach the optimizer service. Check your connection and try again.");
    }

    let data;
    try {
      const text = await res.text();
      data = safeParseOptimizerJson(text);
    } catch {
      if (res.status === 401) {
        throw new Error(
          "Unauthorized (401). Your session may have expired — sign out and log in again, or ask your backend team to allow this route with the same auth as other /promo endpoints."
        );
      }
      throw new Error(`Optimizer failed (${res.status})`);
    }

    if (res.status === 401) {
      throw new Error(
        data?.message ||
          data?.error ||
          "Unauthorized. Sign in again or ensure /promo/optimize accepts your app token (x-access-token / Bearer)."
      );
    }

    if (!res.ok || data?.success === false) {
      console.warn("[promoSimulatorService] getOptimizerResults error", data);
      throwPromoApiError(data, "Failed to optimize plan. Please try again.");
    }

    return normalizeOptimizeApiResponse(data);
  },

  optimizeAndBuildPlannerGrid: async ({
    projectId,
    manufacturer = PROMO_DEFAULT_MANUFACTURER,
    brand,
    productClass,
    start_week,
    end_week,
  }) => {
    if (projectId == null || projectId === "") {
      throw new Error("Project is not selected");
    }
    const body = buildOptimizeRequestBody({
      projectId,
      manufacturer,
      brand,
      productClass,
      start_week,
      end_week,
      constraints: PROMO_OPTIMIZE_DEFAULT_CONSTRAINTS,
      includeTargets: false,
    });

    let res;
    try {
      res = await fetch(`${getUrl()}/promo/optimize`, {
        method: "POST",
        headers: promoRequestHeaders(),
        body: JSON.stringify(body),
      });
    } catch {
      throw new Error("Could not reach the optimizer service. Check your connection and try again.");
    }

    let data;
    try {
      const text = await res.text();
      data = safeParseOptimizerJson(text);
    } catch {
      throw new Error(`Optimizer failed (${res.status})`);
    }

    if (res.status === 401) {
      throw new Error(
        data?.message || data?.error || "Unauthorized. Sign in again to continue."
      );
    }

    if (!res.ok || data?.success === false) {
      throw new Error(promoApiUserMessage(data, "Failed to optimize plan. Please try again."));
    }

    data = normalizeOptimizeApiResponse(data);

    const rawCalendar = Array.isArray(data.calendar_cells)
      ? data.calendar_cells
      : Array.isArray(data.optimized_calendar)
        ? data.optimized_calendar
        : [];

    const isPlannerDetailRow = (item) => {
      if (!item || item.sku_name === "TOTAL") return false;
      if (item.row_type === "SKU_TOTAL") return false;
      if (item.row_type === "DETAIL") {
        const w = item.week;
        return w != null && Number(w) !== 0;
      }
      const w = item.week;
      return w != null && Number(w) !== 0;
    };

    let items = rawCalendar.filter(isPlannerDetailRow);

    if (!items.length && Array.isArray(data.calendar) && data.calendar.length) {
      items = data.calendar
        .map(flatCalendarEntryToPlannerItem)
        .filter(Boolean);
    }

    const rangeStart = Number(data.plan_summary?.start_week ?? start_week);
    const rangeEnd = Number(data.plan_summary?.end_week ?? end_week);

    const weekNums = items
      .map((item) => Number(item.week))
      .filter((w) => Number.isFinite(w) && w > 0);
    const weekSet = new Set(weekNums);

    let weeks = [...weekSet].sort((a, b) => a - b);
    if (Number.isFinite(rangeStart) && Number.isFinite(rangeEnd) && rangeEnd >= rangeStart) {
      const fullRange = weeksFromRange(rangeStart, rangeEnd);
      if (fullRange.length) weeks = fullRange;
    } else if (!weeks.length) {
      weeks = weeksFromRange(start_week, end_week);
    }

    const skuNames = [];
    const skuSeen = new Set();
    for (const item of items) {
      const name = item.sku_name;
      if (!name || skuSeen.has(name)) continue;
      skuSeen.add(name);
      skuNames.push(name);
    }

    const rows = skuNames.map((skuName) => {
      const skuId = `opt_${skuName.replace(/\s+/g, "_")}`;
      const cells = {};
      let skuBrand = brand;
      let skuManufacturer = manufacturer;

      for (const week of weeks) {
        const item = items.find((i) => i.sku_name === skuName && Number(i.week) === week);
        if (item) {
          if (item.brand) skuBrand = item.brand;
          if (item.manufacturer) skuManufacturer = item.manufacturer;
          const band = item.selected_band ?? item.recommended_band ?? "";
          const m = item.metrics || {};
          cells[week] = {
            skuId,
            week,
            selectedBand: item.selected_band ?? null,
            promoType: item.promo_type ?? null,
            discountPct: bandToDiscountPct(band),
            kpis: {
              expectedVolumeL: m.expected_volume_l ?? null,
              expectedProfit: m.expected_profit ?? null,
              discountSpend: m.discount_spend ?? null,
              roi: m.roi ?? null,
              upliftRatio: m.uplift_ratio ?? null,
              contributionMargin: m.contribution_margin ?? null,
              contributionMarginEstimated: m.contribution_margin_estimated ?? null,
              promoPricePl: m.promo_price_pl ?? null,
              promoPriceUnit: m.promo_price_unit ?? null,
              recommendedBand: item.recommended_band ?? null,
              selectedBand: item.selected_band ?? null,
              mustPromote: item.must_promote,
              promoType: item.promo_type ?? null,
              warnings: Array.isArray(m.warnings) ? m.warnings : [],
            },
          };
        }
      }

      const totalItem = rawCalendar.find(
        (item) =>
          String(item?.sku_name) === String(skuName) && item?.row_type === "SKU_TOTAL"
      );
      let skuTotalFromApi = null;
      if (totalItem) {
        const tb = totalItem.selected_band ?? totalItem.recommended_band ?? "";
        const tm = totalItem.metrics || {};
        skuTotalFromApi = {
          discountPct: bandToDiscountPct(tb),
          promoType: totalItem.promo_type ?? null,
          kpis: {
            expectedVolumeL: tm.expected_volume_l ?? null,
            expectedProfit: tm.expected_profit ?? null,
            discountSpend: tm.discount_spend ?? null,
            roi: tm.roi ?? null,
            upliftRatio: tm.uplift_ratio ?? null,
            contributionMargin: tm.contribution_margin ?? null,
            contributionMarginEstimated: tm.contribution_margin_estimated ?? null,
            promoPricePl: tm.promo_price_pl ?? null,
            promoPriceUnit: tm.promo_price_unit ?? null,
            recommendedBand: totalItem.recommended_band ?? null,
            selectedBand: totalItem.selected_band ?? null,
            mustPromote: totalItem.must_promote,
            promoType: totalItem.promo_type ?? null,
            warnings: Array.isArray(tm.warnings) ? tm.warnings : [],
          },
        };
      }

      return {
        skuId,
        skuLabel: skuName,
        brand: skuBrand,
        manufacturer: skuManufacturer,
        cells,
        skuTotalFromApi,
      };
    });

    return {
      id: "recommended",
      name: "Recommended Baseline",
      weeks,
      rows,
      modifiedCells: {},
      planSummary: data.plan_summary ?? null,
      totalRow: data.total_row ?? null,
      source: "optimize_api",
      planId: data.plan_id ?? null,
    };
  },

  comparePromoDiscounts: async ({ rank_by = "roi" } = {}) => {
    const allowed = ["roi", "profit", "volume"];
    const rb = allowed.includes(rank_by) ? rank_by : "roi";
    const body = {
      brand: "AFIA",
      sku_name: "AFIA CORN OIL 750ML",
      week: 12,
      discounts: [0.15, 0.3, 0.4],
      rank_by: rb,
    };
    const res = await fetch(`${getUrl()}/promo/compare`, {
      method: "POST",
      headers: promoRequestHeaders(),
      body: JSON.stringify(body),
    });
    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error(`Compare failed (${res.status})`);
    }
    if (!res.ok || data?.success === false) {
      console.warn("[promoSimulatorService] comparePromoDiscounts error", data);
      throw new Error(promoApiUserMessage(data, PROMO_API_GENERIC_COMPARE));
    }
    const results = Array.isArray(data?.results) ? data.results : [];
    return {
      results,
      rank_by: data?.rank_by ?? rb,
      brand: data?.brand,
      sku_name: data?.sku_name,
      week: data?.week,
    };
  },

  /**
   * PATCH /promo/plan/{planId}/cell
   * Called when the user edits the discount band for a specific SKU / week in the planner.
   * Returns a cell patch object ready to dispatch to updatePlannerCell.
   */
  updatePlannerCellApi: async ({ planId, projectId, skuName, week, selectedDiscount }) => {
    if (!planId) throw new Error("No active plan – run Recommend Plan first.");

    let res;
    try {
      res = await fetch(`${getUrl()}/promo/plan/${encodeURIComponent(planId)}/cell`, {
        method: "PATCH",
        headers: promoRequestHeaders(),
        body: JSON.stringify({
          project_id: Number(projectId),
          sku_name: skuName,
          week: Number(week),
          selected_discount: selectedDiscount,
        }),
      });
    } catch {
      throw new Error("Could not reach the plan service. Check your connection and try again.");
    }

    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error(`Plan cell update failed (${res.status})`);
    }

    if (!res.ok || data?.success === false) {
      throw new Error(promoApiUserMessage(data, "Failed to update cell. Please try again."));
    }

    return normalizePlanCellResponse(data);
  },

  runSimulation: async ({
    projectId,
    manufacturer,
    brand,
    sku_names,
    start_week,
    end_week,
    discountPct,
  }) => {
    if (projectId == null || projectId === "") {
      throw new Error("Project is not selected");
    }
    const names = Array.isArray(sku_names)
      ? sku_names.map((s) => String(s).trim()).filter(Boolean)
      : [];
    if (!manufacturer?.trim() || !brand?.trim() || names.length === 0) {
      throw new Error("Manufacturer, brand, and at least one SKU are required");
    }
    if (start_week == null || end_week == null) {
      throw new Error("Start week and end week are required");
    }
    const start = Number(start_week);
    const end = Number(end_week);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      throw new Error("Week range is invalid");
    }
    if (end < start) {
      throw new Error("End week must be on or after start week");
    }
    const discount = Number(discountPct);
    const body = {
      project_id: Number(projectId),
      manufacturer: String(manufacturer).trim(),
      brand: String(brand).trim(),
      sku_names: names,
      start_week: start,
      end_week: end,
      discount: (Number.isFinite(discount) ? discount : 0) / 100,
    };
    const res = await fetch(`${getUrl()}/promo/simulate`, {
      method: "POST",
      headers: promoRequestHeaders(),
      body: JSON.stringify(body),
    });
    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error(`Simulation failed (${res.status})`);
    }
    if (!res.ok || data?.success === false || !data?.result) {
      console.warn("[promoSimulatorService] runSimulation error", data);
      throw new Error(promoApiUserMessage(data, PROMO_API_GENERIC_SIMULATION));
    }
    const r = data.result;
    return {
      kpis: {
        expectedVolumeL: r.expected_volume_l,
        upliftRatio: r.uplift_ratio,
        expectedProfit: r.expected_profit,
        discountSpend: r.discount_spend,
        roi: r.roi,
        promoPricePerLiter: r.promo_price_pl,
        promoPriceUnit: r.promo_price_unit,
        discountFraction: r.discount,
        band: r.band,
        skuName: r.sku_name,
        week: r.week,
        heatScore: r.heat_score,
        insideCorridor: r.inside_corridor,
        mustPromote: r.must_promote,
        cannibalizationValue: r.cannibalization_value,
        marginImpact: r.margin_impact,
      },
      warnings: Array.isArray(data.warnings)
        ? data.warnings
        : Array.isArray(r.warnings)
          ? r.warnings
          : [],
    };
  },
};
